import PropTypes from "prop-types";
import * as Yup from "yup";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { LoadingButton } from "@mui/lab";
import { Box, Card, Grid, Stack } from "@mui/material";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// components
import { useSnackbar } from "../../../components/snackbar";
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFTimeField,
} from "../../../components/hook-form";
import RHFCalendarField from "src/components/hook-form/RHFCalendarField";
import useGetRegions from "src/hooks/useGetRegions";
import useGetCourseCategories from "src/hooks/useGetCourseCategories";
import useGetInstructors from "src/hooks/useGetInstructors";
import { WeekDays } from "src/utils/constants";
import useGetSession from "src/hooks/useGetSession";
import useUpdateClassSession from "src/hooks/useUpdateClassSession";

// ----------------------------------------------------------------------

EditClassSessionForm.propTypes = {
  isEdit: PropTypes.bool,
  currentUser: PropTypes.object,
};

const NewSessionSchema = Yup.object().shape({
  category: Yup.string().required("Category is required"),
  location: Yup.string().required("Location is required"),
  instructor: Yup.string().required("Instructor is required"),
  payment: Yup.string().required("Payment is required"),
  start_date: Yup.date()
    .required("Start date is required")
    .typeError("This is not a valid date"),
  end_date: Yup.date()
    .required("End date is required")
    .typeError("This is not a valid date"),
  title_en: Yup.string().required("English Title is required"),
  title_de: Yup.string().required("German Title is required"),
  weekly_day: Yup.number()
    .required("Weekly day is required")
    .typeError("Weekly day is required"),
  duration: Yup.string().required("Duration is required"),
  start_time: Yup.date()
    .required("Start time is required")
    .typeError("This is not a valid date"),
  end_time: Yup.date()
    .required("End time is required")
    .typeError("This is not a valid date"),
  slots: Yup.string().required("Slots is required"),
  part: Yup.string().required("Part is required"),
  price: Yup.number()
    .required("Price is required")
    .typeError("This is not a valid price"),
});

const defaultValues = {
  category: "",
  location: "",
  instructor: "",
  start_date: new Date(),
  end_date: new Date(),
  title_en: "",
  title_de: "",
  weekly_day: "",
  duration: "",
  start_time: new Date(),
  end_time: new Date(),
  slots: "",
  part: "",
  price: "",
  payment: "",
};

export default function EditClassSessionForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const methods = useForm({
    resolver: yupResolver(NewSessionSchema),
    defaultValues,
  });
  const {
    reset,
    // watch,
    // control,
    // setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { data: regionsData, isLoading: isRegionsLoading } = useGetRegions();
  const { data: courseCategoriesData, isLoading: isLoadingCourseCategories } =
    useGetCourseCategories();
  const { data: instructorsData, isLoading: isLoadingInstructors } =
    useGetInstructors();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: updateSession, isLoading: isCreatingSession } =
    useUpdateClassSession({
      onSuccess: () => {
        reset();
        enqueueSnackbar("Create success!");
        navigate(PATH_DASHBOARD.classes.sessions);
      },
      onError: () => {
        enqueueSnackbar("Create failed!", {
          variant: "error",
        });
      },
    });

  const regionsItems = useMemo(() => {
    if (Array.isArray(regionsData?.regions)) {
      return regionsData.regions.map((region) => ({
        id: region.id,
        name: region.name,
      }));
    } else {
      return [];
    }
  }, [regionsData]);

  const courseCategoryItems = useMemo(() => {
    if (Array.isArray(courseCategoriesData?.categories)) {
      return courseCategoriesData.categories.map((category) => ({
        id: category.id,
        name: category.name,
      }));
    } else {
      return [];
    }
  }, [courseCategoriesData]);

  const instructorsItems = useMemo(() => {
    if (Array.isArray(instructorsData?.instructors)) {
      return instructorsData?.instructors.map((instructor) => ({
        id: instructor.id,
        name: instructor.displayName,
      }));
    } else {
      return [];
    }
  }, [instructorsData]);

  const { isLoading: isLoadingSession, refetch } = useGetSession(
    {
      id: id,
    },
    {
      enabled: false,
      onSuccess: ({ session }) => {
        let weekly_day = "";
        if (!isNaN(session?.weekly_day)) {
          weekly_day = session.weekly_day + 1;
        }
        reset({
          category: session?.category?.id || "",
          location: session?.regionId || "",
          instructor: session?.instructorId || "",
          title_en: session?.name || "",
          title_de: session?.name_de || "",
          part: session?.part || "",
          start_date: session?.start_date
            ? session.start_date.toDate()
            : new Date(),
          start_time: session?.start_time
            ? session.start_time.toDate()
            : new Date(),
          end_date: session?.end_date ? session.end_date.toDate() : new Date(),
          end_time: session?.end_time ? session.end_time.toDate() : new Date(),
          weekly_day: weekly_day,
          duration: session?.duration || "",
          price: session?.price || "",
          slots: session?.slots || "",
          payment: session?.payment || "",
        });
      },
      onError: () => {
        enqueueSnackbar("Failed to get session", {
          variant: "error",
        });
        navigate("/sessions");
      },
    }
  );

  useEffect(() => {
    if (id) {
      refetch();
    } else {
      navigate("/sessions");
    }
  }, [id, refetch]);

  const isLoading =
    isRegionsLoading ||
    isLoadingCourseCategories ||
    isLoadingInstructors ||
    isCreatingSession ||
    isLoadingSession;

  const onSubmit = (form) => {
    const category = Array.isArray(courseCategoriesData?.categories)
      ? courseCategoriesData.categories.find(
          (category) => category.id === form.category
        )
      : null;

    const region = Array.isArray(regionsData?.regions)
      ? regionsData.regions.find((region) => region.id === form.location)
      : null;

    const instructor = Array.isArray(instructorsData?.instructors)
      ? instructorsData.instructors.find(
          (instructor) => instructor.id === form.instructor
        )
      : null;
    form.weekly_day = parseInt(form.weekly_day) - 1;
    const data = {
      form: form,
      category: category,
      region: region,
      instructor: instructor,
      id: id,
    };
    updateSession(data);
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {isLoading && (
          <Grid item xs={24}>
            <Card
              sx={{ p: 3 }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading...
            </Card>
          </Grid>
        )}
        {!isLoading && (
          <Grid item xs={24}>
            <Card sx={{ p: 3 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                }}
              >
                <RHFSelect
                  native
                  name="category"
                  options={courseCategoryItems}
                  label="Course category name"
                />
                <RHFSelect
                  native
                  name="location"
                  label="Location"
                  options={regionsItems}
                />
                <RHFSelect
                  native
                  name="instructor"
                  label="Instructor"
                  options={instructorsItems}
                />
                <RHFSelect
                  native
                  name="payment"
                  label="Flexible Payment"
                  options={[
                    {
                      id: "one-to-one",
                      name: "One-to-one",
                    },
                    {
                      id: "togther",
                      name: "Togther",
                    },
                  ]}
                />
                <RHFCalendarField name="start_date" label="Start Date" />
                <RHFCalendarField name="end_date" label="End Date" />
                <RHFTextField
                  name="title_en"
                  label="Course Title(In English)"
                />
                <RHFTextField name="title_de" label="Course Title(In German)" />
                <RHFSelect
                  native
                  name="weekly_day"
                  label="Weekly Day"
                  options={WeekDays.map((day, index) => ({
                    id: index + 1,
                    name: day,
                  }))}
                />
                <RHFSelect
                  native
                  name="duration"
                  label="Select Week or month"
                  options={[
                    {
                      id: "week",
                      name: "Every week",
                    },
                    {
                      id: "2nd-week",
                      name: "Every 2nd week",
                    },
                    {
                      id: "3rd-week",
                      name: "Every 3rd week",
                    },
                    {
                      id: "month",
                      name: "Once a month",
                    },
                  ]}
                />
                <RHFTimeField name="start_time" label="Course Start Time" />
                <RHFTimeField name="end_time" label="Course End Time" />
                <RHFTextField name="price" label="Price" />
                <RHFTextField name="slots" label="Slots" />
                <RHFTextField name="part" label="Part" />
              </Box>

              <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {"Save Changes"}
                </LoadingButton>
              </Stack>
            </Card>
          </Grid>
        )}
      </Grid>
    </FormProvider>
  );
}
